"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.EventQueue = exports.PendingContextEvents = exports.PendingEvents = void 0;
const delayed_action_1 = require("./delayed_action");
class PendingEvents {
    constructor(eventCount, queue, events) {
        this.events = [];
        this.eventCount = eventCount;
        this.queue = queue;
        this.events = events;
    }
    async failed(events) {
        return await this.queue.failed(events);
    }
    async finish(events) {
        return await this.queue.finish(events);
    }
    async close() {
        return await this.queue.close(this);
    }
}
exports.PendingEvents = PendingEvents;
class PendingContextEvents {
    constructor(context, events) {
        this.events = [];
        this.context = context;
        this.events = events;
    }
}
exports.PendingContextEvents = PendingContextEvents;
class EventQueue {
    constructor() {
        this.pending = [];
        this.failedToAdd = false;
        this.delayedAction = null;
        // eslint-disable-next-line no-undef
        this.database = null;
        this.transaction = false;
        this.eventCount = 0;
        this.initialize().catch((e) => console.error('Database error:', e));
    }
    async initialize() {
        // eslint-disable-next-line no-undef
        if (typeof indexedDB === 'undefined') {
            this.failedToAdd = true;
            return;
        }
        // eslint-disable-next-line no-undef
        const request = indexedDB.open('EventDB', 1);
        request.onupgradeneeded = () => {
            const store = request.result;
            if (!store.objectStoreNames.contains('events')) {
                store.createObjectStore('events', { keyPath: 'client_id' });
            }
        };
        this.database = await toResult(request);
    }
    async pendingSize() {
        return this.pending.length;
    }
    async size() {
        if (!this.database)
            return -1;
        const transaction = this.database.transaction('events', 'readonly');
        const store = transaction.objectStore('events');
        return await toResult(store.count());
    }
    async add(context, event) {
        this.eventCount++;
        this.pending.push({ client_id: event.client_id, context: context, event: event });
        try {
            await this.queueStore();
        }
        catch (e) {
            this.failedToAdd = true;
            console.error('failed to add event: ', e);
            throw e;
        }
    }
    async get() {
        if (this.failedToAdd) {
            const events = this.pending;
            this.pending = [];
            return this.toPendingEvents(events);
        }
        if (!this.database || this.transaction)
            return null;
        this.transaction = true;
        const transaction = this.database.transaction('events', 'readonly');
        const store = transaction.objectStore('events');
        const events = await toResult(store.getAll());
        if (events.length === 0)
            return new PendingEvents(this.eventCount, this, []);
        return this.toPendingEvents(events);
    }
    toPendingEvents(events) {
        const pendingEvents = [];
        let current = null;
        let currentSessionId = null;
        for (const event of events) {
            if (currentSessionId !== event.context.session_id) {
                currentSessionId = event.context.session_id;
                current = new PendingContextEvents(event.context, []);
                pendingEvents.push(current);
            }
            current.events.push(event.event);
        }
        return new PendingEvents(this.eventCount, this, pendingEvents);
    }
    async failed(events) {
        if (!this.failedToAdd)
            return;
        for (const event of events.events) {
            this.pending.push({ client_id: events.context.client_id, context: events.context, event: event });
        }
    }
    async finish(events) {
        if (this.failedToAdd)
            return;
        const transaction = this.database.transaction('events', 'readwrite');
        const store = transaction.objectStore('events');
        const finished = [];
        for (const event of events.events) {
            finished.push(toResult(store.delete(event.client_id)));
        }
        await Promise.all(finished);
        transaction.commit();
    }
    async close(events) {
        this.transaction = false;
        return this.eventCount !== events.eventCount;
    }
    async queueStore() {
        if (!this.delayedAction) {
            const self = this;
            this.delayedAction = new delayed_action_1.DelayedAction(async () => {
                await self.store();
            }, () => {
                self.delayedAction = null;
            });
        }
        else {
            this.delayedAction.delay();
        }
        try {
            return await this.delayedAction.promise();
        }
        finally {
            this.delayedAction = null;
        }
    }
    async store() {
        if (!this.database) {
            await this.queueStore();
            return;
        }
        if (this.pending.length === 0)
            return;
        const transaction = this.database.transaction('events', 'readwrite');
        const store = transaction.objectStore('events');
        const events = this.pending;
        this.pending = [];
        const added = [];
        for (const pending of events) {
            added.push(toResult(store.add(pending)));
        }
        await Promise.all(added);
        transaction.commit();
    }
}
exports.EventQueue = EventQueue;
// eslint-disable-next-line no-undef
async function toResult(request) {
    return await new Promise((resolve, reject) => {
        request.onsuccess = () => {
            resolve(request.result);
        };
        request.onerror = () => {
            reject(request.error);
        };
    });
}
//# sourceMappingURL=bosca_event_queue.js.map